import React, { useEffect, useMemo, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

export type CreateClipResult = {
    id: string;
    compositionId?: string;
    candidateId?: string;
    title: string;
    description: string;
    durationSec: number;
    startMs?: number;
    endMs?: number;
    previewText?: string;
    score?: number;
};

type CreateClipsResultsSidebarProps = {
    onBack: () => void;
    clips?: CreateClipResult[];
    selectedClipId?: string | null;
    onSelectClip?: (clipId: string) => void;
    onDiscardAll?: () => void;
    onDone?: () => void;
};

const formatDurationLabel = (seconds: number): string => {
    const normalized = Number.isFinite(seconds) ? Math.max(0, Math.round(seconds)) : 0;
    return `${normalized} ${normalized === 1 ? "sec" : "secs"}`;
};

const CreateClipsResultsSidebar: React.FC<CreateClipsResultsSidebarProps> = ({
    onBack,
    clips,
    selectedClipId,
    onSelectClip,
    onDiscardAll,
    onDone,
}) => {
    const rows = useMemo(() => (Array.isArray(clips) ? clips : []), [clips]);
    const effectiveSelectedIndex = useMemo(() => {
        if (rows.length === 0) return -1;
        const selectedIndex = rows.findIndex((clip) => clip.id === selectedClipId);
        return selectedIndex >= 0 ? selectedIndex : 0;
    }, [rows, selectedClipId]);
    const selectedCardRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!selectedCardRef.current) return;
        selectedCardRef.current.scrollIntoView({
            block: "nearest",
            behavior: "smooth",
        });
    }, [effectiveSelectedIndex]);

    const handleSelectIndex = (index: number) => {
        if (index < 0 || index >= rows.length) return;
        const nextClip = rows[index];
        if (!nextClip) return;
        onSelectClip?.(nextClip.id);
    };

    return (
        <Box
            sx={{
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                p: "3px",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "10px",
                    marginLeft: "8px",
                    pr: "4px",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton size="small" onClick={onBack} sx={{ p: 0.5 }}>
                        <ArrowBackRoundedIcon fontSize="small" />
                    </IconButton>
                    <Typography sx={{ fontSize: 15, fontWeight: 600, color: "var(--app-text-primary)" }}>
                        Create clips
                    </Typography>
                </Box>

                {rows.length > 0 ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "2px", flexShrink: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "var(--app-text-primary)", minWidth: 44, textAlign: "center" }}>
                            {effectiveSelectedIndex + 1} / {rows.length}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => handleSelectIndex(effectiveSelectedIndex - 1)}
                            disabled={effectiveSelectedIndex <= 0}
                            sx={{ width: 28, height: 28 }}
                        >
                            <KeyboardArrowUpRoundedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => handleSelectIndex(effectiveSelectedIndex + 1)}
                            disabled={effectiveSelectedIndex >= rows.length - 1}
                            sx={{ width: 28, height: 28 }}
                        >
                            <KeyboardArrowDownRoundedIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ) : null}
            </Box>

            <Box sx={{ px: "8px" }}>
                <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: "var(--app-text-muted)" }}>
                    Your clips have been saved as new compositions. Click the items below to preview them.
                </Typography>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    pr: "2px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    scrollbarWidth: "thin",
                }}
            >
                {rows.length > 0 ? (
                    rows.map((clip, index) => {
                        const isSelected = index === effectiveSelectedIndex;

                        return (
                            <Box
                                key={clip.id}
                                ref={isSelected ? selectedCardRef : null}
                                role="button"
                                tabIndex={0}
                                onClick={() => onSelectClip?.(clip.id)}
                                onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        onSelectClip?.(clip.id);
                                    }
                                }}
                                sx={{
                                    backgroundColor: "var(--app-bg-surface)",
                                    border: "1px solid",
                                    borderColor: isSelected ? "#60A5FA" : "var(--app-border-subtle)",
                                    borderRadius: "10px",
                                    px: "10px",
                                    py: "10px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "6px",
                                    cursor: "pointer",
                                    transition: "border-color 0.12s ease, box-shadow 0.12s ease",
                                    boxShadow: isSelected
                                        ? "inset 0 0 0 1px rgba(96, 165, 250, 0.35)"
                                        : "none",
                                    outline: "none",
                                    "&:hover": {
                                        borderColor: isSelected ? "#60A5FA" : "var(--app-border-muted)",
                                    },
                                }}
                            >
                                <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, color: "var(--app-text-primary)" }}>
                                    {clip.title}
                                </Typography>

                                <Typography sx={{ fontSize: 12.5, lineHeight: 1.45, color: "var(--app-text-muted)" }}>
                                    {clip.description}
                                </Typography>

                                <Typography sx={{ fontSize: 12.5, color: "var(--app-text-muted)", pt: "2px" }}>
                                    {formatDurationLabel(clip.durationSec)}
                                </Typography>
                            </Box>
                        );
                    })
                ) : (
                    <Box
                        sx={{
                            backgroundColor: "var(--app-bg-surface)",
                            border: "1px solid var(--app-border-subtle)",
                            borderRadius: "10px",
                            px: "10px",
                            py: "10px",
                        }}
                    >
                        <Typography sx={{ fontSize: 13.5, color: "var(--app-text-muted)", lineHeight: 1.5 }}>
                            No generated clips to review yet.
                        </Typography>
                    </Box>
                )}

                <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", pt: "2px" }}>
                    <Button
                        onClick={onDiscardAll}
                        disabled={rows.length === 0}
                        startIcon={<CloseRoundedIcon sx={{ fontSize: 16 }} />}
                        sx={{
                            justifyContent: "flex-start",
                            textTransform: "none",
                            color: "var(--app-text-primary)",
                            fontSize: 13,
                            fontWeight: 500,
                            borderRadius: "8px",
                            minHeight: 36,
                            px: "10px",
                            "&:hover": {
                                backgroundColor: "rgba(17, 24, 39, 0.04)",
                            },
                        }}
                    >
                        Discard all
                    </Button>

                    <Button
                        onClick={onDone}
                        disabled={rows.length === 0}
                        startIcon={<CheckRoundedIcon sx={{ fontSize: 16 }} />}
                        sx={{
                            justifyContent: "flex-start",
                            textTransform: "none",
                            color: "var(--app-text-primary)",
                            fontSize: 13,
                            fontWeight: 500,
                            borderRadius: "8px",
                            minHeight: 36,
                            px: "10px",
                            "&:hover": {
                                backgroundColor: "rgba(17, 24, 39, 0.04)",
                            },
                        }}
                    >
                        Done
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default CreateClipsResultsSidebar;
